import axios from 'axios';
import { customerLogoUrls } from 'config/urls';

/**
 * One entry in the customer logo catalogue.
 *
 * `image_name` is the stable identity — it is what set_logo takes. `file_url` is presigned,
 * minted per request and expires, so it must never be cached or parsed for a name.
 */
export type CustomerLogoItem = {
    image_name: string;
    file_url: string;
    /** Server-side marker. The UI deliberately ignores this — only current_selected drives rendering. */
    default: boolean;
    current_selected: boolean;
    content_type: string;
    size_bytes: number;
    original_filename: string | null;
    updated_at: string | null;
};

type FetchLogosResponse = { logos: CustomerLogoItem[]; count: number };
type SingleLogoResponse = { logo: CustomerLogoItem };

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * The API returns two error shapes. Application errors (400/404/409/413/415) carry a string
 * `detail` that is safe to show; framework schema errors (422) carry a list. A 500 `detail` is
 * never user-facing copy, so it is swapped for a generic message.
 */
export function extractLogoApiError(error: any, fallback: string = GENERIC_ERROR): string {
    const status = error?.response?.status;
    if (status === 500) return fallback;

    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;

    if (Array.isArray(detail) && detail.length) {
        const [first] = detail;
        const location = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : '';
        const message = first?.msg || 'Invalid value';
        return location ? `${location}: ${message}` : message;
    }

    return fallback;
}

/** The whole catalogue, newest first. An empty catalogue is a 200 with `logos: []`, not a 404. */
export async function fetchCustomerLogos(): Promise<CustomerLogoItem[]> {
    const { data } = await axios.get<FetchLogosResponse>(customerLogoUrls.fetchLogos);
    return data?.logos ?? [];
}

/**
 * The logo flagged `current_selected`, or null when there is none.
 *
 * A 404 is a state rather than a failure: it is what the server answers before anyone has
 * selected a logo, and there is also a sub-second window during set_logo where no row carries
 * the flag. Both resolve to null so callers fall back to the bundled asset.
 */
export async function fetchCurrentCustomerLogo(): Promise<CustomerLogoItem | null> {
    try {
        const { data } = await axios.get<SingleLogoResponse>(customerLogoUrls.getCurrentLogo);
        return data?.logo ?? null;
    } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
    }
}

/**
 * Creates a logo, or replaces the image of an existing `image_name` while leaving its flags
 * alone. Uploading never changes which logo is selected — that is set_logo's job.
 *
 * Content-Type is deliberately not set: axios derives the multipart boundary from the FormData,
 * and the app's global request interceptor attaches the bearer token.
 */
export async function uploadCustomerLogo(imageName: string, file: File): Promise<CustomerLogoItem> {
    const formData = new FormData();
    formData.append('image_name', imageName.trim());
    formData.append('file', file);
    const { data } = await axios.post<SingleLogoResponse>(customerLogoUrls.uploadLogo, formData);
    return data?.logo;
}

/**
 * Makes this the logo the app renders. This is an exclusive flip: `current_selected` is cleared on
 * every other logo and set on this one.
 *
 * `set_default` is never sent. A `false` would be a no-op anyway, and the default flag is not
 * surfaced or managed by this UI — the server owns it.
 */
export async function setCurrentCustomerLogo(imageName: string): Promise<CustomerLogoItem> {
    const { data } = await axios.post<SingleLogoResponse>(customerLogoUrls.setLogo, {
        image_name: imageName,
        set_current: true,
    });
    return data?.logo;
}

/**
 * Renames the catalogue entry. Both flags are preserved.
 *
 * Azure Blob has no rename, so the stored image is not moved and its path keeps the original
 * slug — `file_url` will still contain the old name. That is expected; never parse a name out of
 * the URL. Renaming to the same name (after trimming) is an idempotent 200.
 */
export async function renameCustomerLogo(oldName: string, newName: string): Promise<CustomerLogoItem> {
    const { data } = await axios.post<SingleLogoResponse>(customerLogoUrls.renameLogo, {
        old_name: oldName,
        new_name: newName.trim(),
    });
    return data?.logo;
}
